import { TaskState } from 'dy-frontend-http-repository/lib/data/enums';

export type ClosedParentMode = 'archived' | 'delivered' | 'paused';

export interface ClosedParentNotice {
    mode: ClosedParentMode;
    title: string;
    message: string;
}

export interface ResolveClosedParentNoticeParams {
    state: TaskState | string;
    pausedAt: string | null;
    archivedAt: string | null;
}

// Every new-write control is HIDDEN on a closed parent rather than disabled, which matches the
// established isTaskDelivered idiom but leaves the page silently emptier than the designer
// expects. Saying why, once and in one place, is the difference between "this Request is paused"
// and "the buttons are broken".
//
// Each mode is named explicitly rather than collapsed into one "closed" message: paused is
// temporary and someone can unpause it, delivered is the normal end of the work, and archived is
// an administrative action. They call for different next steps.
//
// Precedence runs most-terminal first. An archived Request may also be delivered, and a delivered
// one may still carry a pause; naming the outermost state is what makes the advice correct.
const NOTICES: { [mode in ClosedParentMode]: Omit<ClosedParentNotice, 'mode'> } = {
    archived: {
        title: 'This Request is archived',
        message:
            'Sub-tasks are read-only while the Request is archived: nothing can be created, edited, reassigned, ' +
            'restored or started here. A timer already running on one of these phases can still be stopped and ' +
            'its work report saved.',
    },
    delivered: {
        title: 'This Request is delivered',
        message:
            'Sub-tasks are read-only now the Request is delivered: nothing can be created, edited, reassigned, ' +
            'archived or started here. A timer already running on one of these phases can still be stopped and ' +
            'its work report saved.',
    },
    paused: {
        title: 'This Request is paused',
        message:
            'Sub-tasks are read-only while the Request is paused: nothing can be created, edited, reassigned, ' +
            'archived or started here. A timer already running on one of these phases can still be stopped and ' +
            'its work report saved. Resume the Request to make changes again.',
    },
};

export const resolveClosedParentNotice = ({
    state,
    pausedAt,
    archivedAt,
}: ResolveClosedParentNoticeParams): ClosedParentNotice | null => {
    const mode: ClosedParentMode | null =
        archivedAt !== null ? 'archived' : state === TaskState.DELIVERED ? 'delivered' : pausedAt !== null ? 'paused' : null;

    if (mode === null) {
        return null;
    }

    return { mode, ...NOTICES[mode] };
};
